//ORDER IS IMPORTANT
import "Utils/WebpackPublicPath";

import "Validation/yup/methods";

import "Styles/UIKit/uikit.scss";
import "Styles/EnterprisePortal/styles.scss";

import React, { Suspense, useEffect, useState } from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import mapboxgl from "mapbox-gl";
import { LayoutContextProvider } from "Components/LayoutContext";
import LsLoading from "Components/LsLoading";
import { EnterpriseAccountClient, PortalClient, IAccountInfoViewModel, BrandedPortalType, UserClient } from "Services/API/API";
import assertContainerElement from "Utils/assertContainerElement";
import UserContext, { IEnterpriseAccountPortalUser } from "./UserContext";
import AccountContext from "./AccountContext";
import { ConnectionsContextProvider } from "Components/Connections/ConnectionsContext";

const root = document.getElementById("EnterprisePortalRoot");
assertContainerElement(root);

const accountId = root.dataset["accountid"]!;
const accountUrl = root.dataset["accounturl"]!;

const Mobility2 = React.lazy(() => import("./Components/Layout/Mobility2"));
const Mobility3 = React.lazy(() => import("./Components/Layout/Mobility3"));

const ApplicationRoot: React.FC = function () {
   const [user, setUser] = useState<IEnterpriseAccountPortalUser>();
   const [account, setAccount] = useState<IAccountInfoViewModel>();

   useEffect(() => {
      const userClient = new UserClient();
      const enterprisePortalClient = new PortalClient();
      const enterpriseAccountClient = new EnterpriseAccountClient();

      Promise.all([
         userClient.currentMember(),
         enterprisePortalClient.currentMember(accountId),
         enterpriseAccountClient.accountInfo(accountId),
      ]).then(all => {
         const [user, enterpriseUser, account] = all;

         mapboxgl.accessToken = LiquidSpaceConfig.MapboxAPIKey || "";
         setUser({ user, enterpriseUser: enterpriseUser});
         setAccount(account);

         document.title = account.name || "Enterprise Portal";
      });
   }, []);

   async function refreshAccount() {
      const enterpriseAccountClient = new EnterpriseAccountClient();
      setAccount(await enterpriseAccountClient.accountInfo(accountId));
   }

   async function refreshUser() {
      const userClient = new UserClient();
      const enterprisePortalClient = new PortalClient();
      const [user, enterpriseUser] = await Promise.all([
         userClient.currentMember(),
         enterprisePortalClient.currentMember(accountId)
      ]);
      const newValue = { user, enterpriseUser};
      setUser(newValue);
      return newValue;
   }

   return (
      user && account
         ?
         <Provider store={LSStore.instance}>
            <UserContext.Provider value={{ ...user, refresh: refreshUser }}>
               <AccountContext.Provider value={{ ...account, refresh: refreshAccount }}>
                  <LayoutContextProvider>
                     <ConnectionsContextProvider preloadConnections>
                        <Suspense fallback={<LsLoading isLoading={true} />}>
                           {account.portalType != BrandedPortalType.Mobility3
                              ? <Mobility2 />
                              : <Mobility3 accountUrl={accountUrl} />}
                        </Suspense>
                     </ConnectionsContextProvider>
                  </LayoutContextProvider>
               </AccountContext.Provider>
            </UserContext.Provider>
         </Provider>
         : <LsLoading isLoading={true} />
   );
}

render(<ApplicationRoot />, root);
